import React from 'react'
import { useTranslation } from 'next-i18next'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import Bugsnag, { NotifiableError } from '@bugsnag/js'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'

import useFlow from 'modules/flow/state/use-flow'
import useOrder from 'modules/order/state/order/use-order'
import InputField from 'modules/ui/molecules/FormComponents/InputField'
import FlowTwoColumnStepLayout from 'modules/flow/components/two-column-step-layout/two-column-step-layout'
import Paragraph from 'modules/ui/atoms/Typography/Paragraph'
import TokenexField from './components/tokenex-field'
import { CheckExample } from './components/check-example'
import {
  trackFormErrorEvent,
  trackFormSubmitEvent,
} from '../../../analytics/helpers/track-form-events'
import { FormWithoutStyles } from '../shared/form.styled'
import { StatusType } from 'modules/status/state/status-types'
import useStatus from 'modules/status/state/use-status'

import useTrackUIElementEvent from 'modules/analytics/hooks/useTrackUIElementEvent'
import mParticleSdk from '@mparticle/web-sdk'

interface FormData {
  accountNumber: string
  routingNumber: string
}

const BankDetailsStep = () => {
  const flow = useFlow()
  const order = useOrder()
  const status = useStatus()

  const trackEvent = useTrackUIElementEvent()

  const { t } = useTranslation()

  const validationSchema = Yup.object().shape({
    routingNumber: Yup.string()
      .required(
        t('bankDetailsStep.validation.routingNumberRequired', 'Routing number is required.'),
      )
      .matches(
        /^[0-9]{9}$/,
        t(
          'bankDetailsStep.validation.routingNumberInvalid',
          'Please enter a valid routing number.',
        ),
      ),
    accountNumber: Yup.string().required(
      t('bankDetailsStep.validation.accountNumberRequired', 'Account number is required.'),
    ),
  })

  const form = useForm<FormData>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
    shouldFocusError: true,
    values: {
      accountNumber: '',
      routingNumber: '',
    },
  })

  const onSubmit = async (data: FormData) => {
    trackFormSubmitEvent(flow.currentStep.name)

    try {
      status.set(StatusType.SUBMITTING)

      order.address.setValue({
        ...order.address.value,
        country: 'US', // Only US bank accounts work with ACH
      })

      trackEvent('Donation Payment Details Submitted', mParticleSdk.EventType.Other)

      order.paymentDetails.setMethod({
        accountNumber: data.accountNumber,
        routingNumber: data.routingNumber,
      })

      flow.nextStep && flow.goToStep(flow.nextStep.name)
      status.set(StatusType.IDLE)
    } catch (e) {
      console.error(e)

      Bugsnag.notify(e as NotifiableError)

      status.set(
        StatusType.FAILED,
        t(
          'bankDetailsStep.paymentError',
          'An error has occurred while trying to finalize your payment.',
        ),
      )
    }
  }

  const onError = async (errors: FieldErrors) => {
    const firstError = Object.keys(errors)[0] as keyof FormData

    trackFormErrorEvent(flow.currentStep.name, firstError)

    form.setFocus(firstError)
  }

  return (
    <FormProvider {...form}>
      <FormWithoutStyles onSubmit={form.handleSubmit(onSubmit, onError)}>
        <>
          <FlowTwoColumnStepLayout.Header
            title={flow.currentStep.title}
            backButtonDataId="bank-details-step-back-button"
          />
          <FlowTwoColumnStepLayout.Body>
            <Paragraph style={{ textAlign: 'center', marginBottom: 16 }}>
              {t(
                'bankDetailsStep.paragraph.accountInfo',
                'Please provide your account and routing number located at the bottom of your check or in your online banking portal.',
              )}
            </Paragraph>
            <CheckExample />
            <InputField
              required
              dataId="bank-routing-number-input"
              id="routingNumber"
              label={t('bankDetailsStep.labels.routingNumber', 'Routing number')}
              error={form.formState.errors.routingNumber as { message: string }}
            />
            <TokenexField
              type="ACH"
              id="accountNumber"
              label={t('bankDetailsStep.labels.accountNumber', 'Account number')}
              error={form.formState.errors.accountNumber as { message: string }}
            />
          </FlowTwoColumnStepLayout.Body>
          <FlowTwoColumnStepLayout.Footer>
            <FlowTwoColumnStepLayout.FooterButton
              size="lg"
              type="submit"
              dataId="bank-details-step-continue-button"
              disabled={
                status.type === StatusType.SUBMITTING || status.type === StatusType.PROCESSING
              }
            >
              {t('bankDetailsStep.buttons.continue', 'Continue')}
            </FlowTwoColumnStepLayout.FooterButton>
          </FlowTwoColumnStepLayout.Footer>
        </>
      </FormWithoutStyles>
    </FormProvider>
  )
}

export default BankDetailsStep
